import type { PrtgReadinessNode } from "../../questionnaires";

/**
 * What each cluster-readiness channel MEANS, so the flyout can show "Storage:
 * node filesystem 57 %" instead of an opaque "readiness-sensor" row.
 *
 * Source of truth for channels, units and thresholds: readiness.sh
 * `prtg_channel_meta()` and opsanio-helm-cluster-readiness
 * `files/PRTG_CHANNELS.md` (mirrored in functions/prtg/api.ts
 * READINESS_CHANNEL_LIMITS). Channels readiness.sh pushes WITHOUT a limit get
 * no invented threshold: only where the meaning itself decides (a faulted
 * volume, a lost quorum, a flag that is 0) is a value judged; everything else
 * is shown as information.
 *
 * Pure module (no React) so it can be unit-tested.
 */

export type ReadinessGroup =
  | "cluster"
  | "etcd"
  | "dns"
  | "opensearch"
  | "storage"
  | "longhorn"
  | "memory"
  | "cpu"
  | "time"
  | "certs"
  | "other";

export type ReadinessKind =
  | "flag"
  | "percent"
  | "seconds"
  | "gib"
  | "cores"
  | "count"
  | "days"
  | "raw";

export type ReadinessStatus = "ok" | "warning" | "error" | "info" | "missing";

interface Thresholds {
  warnBelow?: number;
  errorBelow?: number;
  warnAbove?: number;
  errorAbove?: number;
}

export interface ReadinessChannelDef {
  /** Stable id, also the i18n key suffix. */
  key: string;
  group: ReadinessGroup;
  kind: ReadinessKind;
  thresholds?: Thresholds;
  /** Flag channels that are fine at 0 (e.g. a worker is no etcd member). */
  informational?: boolean;
  /** Channel whose value is shown as a detail line under this one. */
  detail?: { used: string; total?: string };
}

/** Channels readiness.sh pushes, keyed by channel name. */
const DEFS: Record<string, ReadinessChannelDef> = {
  // --- Cluster
  Ready: { key: "ready", group: "cluster", kind: "flag", thresholds: { errorBelow: 1 } },
  ApiServer: { key: "apiServer", group: "cluster", kind: "flag", thresholds: { errorBelow: 1 } },
  NodesReady: {
    key: "nodesReady",
    group: "cluster",
    kind: "percent",
    thresholds: { warnBelow: 100, errorBelow: 67 },
  },
  NodesReachable: {
    key: "nodesReachable",
    group: "cluster",
    kind: "percent",
    thresholds: { warnBelow: 100, errorBelow: 67 },
  },
  NodeReady: { key: "nodeReady", group: "cluster", kind: "flag", thresholds: { errorBelow: 1 } },
  // A count of adverse node conditions: any is worth a look.
  NodeConditions: { key: "nodeConditions", group: "cluster", kind: "count", thresholds: { warnAbove: 0 } },
  NodeConditionsLocal: {
    key: "nodeConditionsLocal",
    group: "cluster",
    kind: "count",
    thresholds: { warnAbove: 0 },
  },
  // --- etcd
  EtcdQuorum: { key: "etcdQuorum", group: "etcd", kind: "flag", thresholds: { errorBelow: 1 } },
  EtcdMember: { key: "etcdMember", group: "etcd", kind: "flag", informational: true },
  EtcdVoters: { key: "etcdVoters", group: "etcd", kind: "count" },
  EtcdHealthyVoters: { key: "etcdHealthyVoters", group: "etcd", kind: "count" },
  // 0 = the next member failure loses quorum.
  EtcdFaultTolerance: {
    key: "etcdFaultTolerance",
    group: "etcd",
    kind: "count",
    thresholds: { warnBelow: 1 },
  },
  // --- DNS
  Dns: { key: "dns", group: "dns", kind: "flag", thresholds: { errorBelow: 1 } },
  DnsCustomer: { key: "dnsCustomer", group: "dns", kind: "flag", thresholds: { errorBelow: 1 } },
  // --- OpenSearch (channel name typo is readiness.sh's)
  Openensearch: {
    key: "opensearchShards",
    group: "opensearch",
    kind: "percent",
    thresholds: { warnBelow: 100, errorBelow: 90 },
  },
  // --- Storage
  Nodefs: {
    key: "nodefs",
    group: "storage",
    kind: "percent",
    thresholds: { warnAbove: 80, errorAbove: 90 },
    detail: { used: "NodefsUsedGib" },
  },
  Imagefs: {
    key: "imagefs",
    group: "storage",
    kind: "percent",
    thresholds: { warnAbove: 80, errorAbove: 90 },
    detail: { used: "ImagefsUsedGib" },
  },
  VolumeFillPercent: { key: "volumeFill", group: "storage", kind: "percent" },
  VolumesOverLimit: { key: "volumesOverLimit", group: "storage", kind: "count", thresholds: { warnAbove: 0 } },
  // --- Longhorn
  LonghornVolumes: { key: "longhornVolumes", group: "longhorn", kind: "count" },
  LonghornVolumesDegraded: {
    key: "longhornDegraded",
    group: "longhorn",
    kind: "count",
    thresholds: { warnAbove: 0 },
  },
  LonghornVolumesFaulted: {
    key: "longhornFaulted",
    group: "longhorn",
    kind: "count",
    thresholds: { errorAbove: 0 },
  },
  LonghornVolumesUnknown: {
    key: "longhornUnknown",
    group: "longhorn",
    kind: "count",
    thresholds: { warnAbove: 0 },
  },
  LonghornVolumesTransitioning: { key: "longhornTransitioning", group: "longhorn", kind: "count" },
  // --- Memory / CPU / time
  RamPercent: {
    key: "ram",
    group: "memory",
    kind: "percent",
    thresholds: { warnAbove: 85, errorAbove: 95 },
    detail: { used: "RamUsedGib", total: "RamTotalGib" },
  },
  CpuUsageCores: { key: "cpuCores", group: "cpu", kind: "cores" },
  MaxTimeDrift: {
    key: "timeDrift",
    group: "time",
    kind: "seconds",
    thresholds: { warnAbove: 5, errorAbove: 10 },
  },
  // --- Certificates (no threshold is pushed - shown as information)
  MinCertExpiryDays: { key: "certMin", group: "certs", kind: "days" },
  CertExpiryDaysTargetApiserver: { key: "certApiserver", group: "certs", kind: "days" },
  CertExpiryDaysTargetEtcdClient: { key: "certEtcdClient", group: "certs", kind: "days" },
  CertExpiryDaysTargetKubelet: { key: "certKubelet", group: "certs", kind: "days" },
};

/** Shown only as the detail line of another channel. */
const DETAIL_ONLY = new Set(["RamUsedGib", "RamTotalGib", "NodefsUsedGib", "ImagefsUsedGib"]);

/** PRTG's default downtime channel and leftover test channels. */
const IGNORED = (name: string): boolean =>
  name === "Ausfallzeit" || name === "Downtime" || /^DIAGTEST/i.test(name);

/** Per-node reachability: "NodeReachableAddress10.0.0.5" (older: "…=10.0.0.5"). */
const REACHABILITY = /^NodeReachable(?:Address)?=?(\d{1,3}(?:\.\d{1,3}){3})$/;

export const GROUP_ORDER: ReadinessGroup[] = [
  "cluster",
  "etcd",
  "dns",
  "opensearch",
  "storage",
  "longhorn",
  "memory",
  "cpu",
  "time",
  "certs",
  "other",
];

export const evaluate = (
  def: Pick<ReadinessChannelDef, "kind" | "thresholds" | "informational">,
  value: number | null,
): ReadinessStatus => {
  if (value === null) return "missing";
  const t = def.thresholds;
  if (!t || def.informational) return "info";
  if (t.errorBelow !== undefined && value < t.errorBelow) return "error";
  if (t.errorAbove !== undefined && value > t.errorAbove) return "error";
  if (t.warnBelow !== undefined && value < t.warnBelow) return "warning";
  if (t.warnAbove !== undefined && value > t.warnAbove) return "warning";
  return "ok";
};

const RANK: Record<ReadinessStatus, number> = {
  error: 4,
  warning: 3,
  ok: 2,
  info: 1,
  missing: 0,
};

export const worst = (statuses: ReadinessStatus[]): ReadinessStatus =>
  statuses.reduce<ReadinessStatus>(
    (acc, s) => (RANK[s] > RANK[acc] ? s : acc),
    "missing",
  );

export interface ReadinessCell {
  value: number | null;
  status: ReadinessStatus;
  /** Detail values (e.g. used / total GiB), when the channel has them. */
  used?: number | null;
  total?: number | null;
  /** PRTG's own formatted text, for unknown channels. */
  display?: string;
}

export interface ReadinessRow {
  /** Channel definition key ("nodefs"), or the raw name for unknown ones. */
  key: string;
  channel: string;
  kind: ReadinessKind;
  thresholds?: Thresholds;
  /** For per-node reachability rows: the pinged node's IP. */
  ip?: string;
  /** One cell per node, in `nodes` order. */
  cells: ReadinessCell[];
  status: ReadinessStatus;
}

export interface ReadinessGroupView {
  group: ReadinessGroup;
  rows: ReadinessRow[];
  status: ReadinessStatus;
}

/**
 * Turn the per-node channel lists into groups of rows with one cell per node.
 * Rows keep the catalogue order; groups follow GROUP_ORDER; empty groups are
 * dropped.
 */
export const buildReadinessGroups = (
  nodes: PrtgReadinessNode[],
): ReadinessGroupView[] => {
  const channelMaps = nodes.map(
    (n) => new Map(n.channels.map((c) => [c.name, c] as const)),
  );
  const valueOf = (i: number, name: string): number | null =>
    channelMaps[i].get(name)?.value ?? null;

  const known: string[] = Object.keys(DEFS);
  const present = new Set(nodes.flatMap((n) => n.channels.map((c) => c.name)));

  const rows: Array<ReadinessRow & { group: ReadinessGroup }> = [];

  for (const name of known) {
    if (!present.has(name)) continue;
    const def = DEFS[name];
    const cells = nodes.map((_, i): ReadinessCell => {
      const value = valueOf(i, name);
      return {
        value,
        status: evaluate(def, value),
        used: def.detail ? valueOf(i, def.detail.used) : undefined,
        total: def.detail?.total ? valueOf(i, def.detail.total) : undefined,
      };
    });
    rows.push({
      key: def.key,
      channel: name,
      kind: def.kind,
      thresholds: def.thresholds,
      group: def.group,
      cells,
      status: worst(cells.map((c) => c.status)),
    });
  }

  // etcd: fewer healthy voters than voters is a degraded cluster, even though
  // no limit is pushed for either count.
  const healthy = rows.find((r) => r.channel === "EtcdHealthyVoters");
  const voters = rows.find((r) => r.channel === "EtcdVoters");
  if (healthy && voters) {
    healthy.cells = healthy.cells.map((c, i) => {
      const total = voters.cells[i].value;
      return c.value !== null && total !== null && c.value < total
        ? { ...c, status: "warning" }
        : c.value !== null && total !== null
          ? { ...c, status: "ok" }
          : c;
    });
    healthy.status = worst(healthy.cells.map((c) => c.status));
  }

  // Per-node reachability: one row per pinged IP, a cell per reporting node.
  const ips = new Map<string, string[]>();
  for (const name of present) {
    const m = REACHABILITY.exec(name);
    if (m) ips.set(m[1], [...(ips.get(m[1]) ?? []), name]);
  }
  [...ips.keys()]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    .forEach((ip) => {
      const names = ips.get(ip)!;
      const cells = nodes.map((_, i): ReadinessCell => {
        const value = names
          .map((n) => valueOf(i, n))
          .find((v) => v !== null) ?? null;
        return { value, status: evaluate({ kind: "flag", thresholds: { errorBelow: 1 } }, value) };
      });
      rows.push({
        key: "nodeReachable",
        channel: `NodeReachable ${ip}`,
        kind: "flag",
        thresholds: { errorBelow: 1 },
        ip,
        group: "cluster",
        cells,
        status: worst(cells.map((c) => c.status)),
      });
    });

  // Anything readiness.sh pushes that this catalogue does not know yet.
  for (const name of [...present].sort()) {
    if (DEFS[name] || DETAIL_ONLY.has(name) || IGNORED(name) || REACHABILITY.test(name)) {
      continue;
    }
    const cells = nodes.map((_, i): ReadinessCell => {
      const c = channelMaps[i].get(name);
      return { value: c?.value ?? null, status: c ? "info" : "missing", display: c?.display };
    });
    rows.push({ key: name, channel: name, kind: "raw", group: "other", cells, status: "info" });
  }

  return GROUP_ORDER.map((group) => {
    const groupRows = rows.filter((r) => r.group === group);
    return {
      group,
      rows: groupRows,
      status: worst(groupRows.map((r) => r.status)),
    };
  }).filter((g) => g.rows.length > 0);
};
